import type {
  FormFieldGroup,
  FormFieldGroupFragment,
  FormHiddenFieldGroup,
} from '@seek/cmsu-cms-connect';
import type { UseField } from '@seek/forms-ui/lib/Field/useField';
import type { Language } from '@seek/forms-ui/lib/types';
import { Box, Heading, Stack, Text } from 'braid-design-system';
import type React from 'react';

import { RenderField } from './RenderField';
import { RenderHiddenField } from './RenderHiddenField';
import { filterVisibleNestedFields, useFormFieldFiltering } from './helper';
import type { FormField, NonHiddenFormField } from './types';

interface FormFieldsRendererProps {
  fields: FormField[];
  useField: UseField<any, Language>;
}

interface RenderFieldGroupProps {
  field: { __typename: 'FormFieldGroup' } & FormFieldGroupFragment;
  key: number;
  useField: UseField<any, Language>;
}

interface RenderSingleFieldProps {
  field: FormField;
  key: number;
  useField: UseField<any, Language>;
}

export const FormFieldsRenderer: React.FC<FormFieldsRendererProps> = ({
  fields,
  useField,
}) => {
  const safeFields = Array.isArray(fields) ? fields : [];
  const { nonHiddenFields, hiddenFieldGroups } =
    useFormFieldFiltering(safeFields);

  return (
    <>
      {nonHiddenFields.map((field, key) => {
        if (field.__typename === 'FormFieldGroup') {
          if (!field) {
            return null;
          }
          return renderFieldGroup({ field, key, useField });
        }
        return renderSingleField({ field, key, useField });
      })}
      {hiddenFieldGroups.map((fieldGroup, key) => (
        <RenderHiddenField
          key={key}
          field={fieldGroup as FormHiddenFieldGroup}
        />
      ))}
    </>
  );
};

const renderFieldGroup = ({ field, key, useField }: RenderFieldGroupProps) => {
  const visibleNestedFields = filterVisibleNestedFields(
    field as FormFieldGroup,
  );

  return (
    <Box key={key} paddingY="xsmall">
      <Stack space="gutter">
        <Heading level="3">{field.formGroupTitle}</Heading>
        <Text>{field.formGroupDescription}</Text>
        {visibleNestedFields.map((innerField, innerKey: number) => (
          <Box key={innerKey}>
            <RenderField
              field={innerField as unknown as NonHiddenFormField}
              key={innerKey}
              useField={useField}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const renderSingleField = ({
  field,
  key,
  useField,
}: RenderSingleFieldProps) => (
  <Box key={key}>
    <RenderField
      field={field as NonHiddenFormField}
      key={key}
      useField={useField}
    />
  </Box>
);
